import React, { useCallback, useEffect, useState } from "react";
import {
    AssistRuntimeSettings,
    PlatformAiSettings,
    UsageSummary,
    fetchAssistPlatformSettings,
    fetchAssistRuntimeSettings,
    fetchAssistUsageSummary,
    saveAssistPlatformSettings,
    saveAssistRuntimeSettings,
} from "../services/polarisService";
import AssistAllowedDomainsField from "./AssistAllowedDomainsField";
import { assistLimitFields, assistRateFields } from "./assistUsageFields";
import "../styles/polaris-admin.css";

type Period = "daily" | "monthly" | "yearly";
type PageTab = "usage" | "configuration";

function formatUsd(value: number | undefined): string {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
}

export default function PolarisAssistAdminPage() {
    const [tab, setTab] = useState<PageTab>("usage");
    const [period, setPeriod] = useState<Period>("daily");
    const [summary, setSummary] = useState<UsageSummary | null>(null);
    const [settings, setSettings] = useState<PlatformAiSettings | null>(null);
    const [runtime, setRuntime] = useState<AssistRuntimeSettings | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const loadUsage = useCallback(async (selected: Period) => {
        setError(null);
        try {
            const [usage, platform] = await Promise.all([
                fetchAssistUsageSummary(selected),
                fetchAssistPlatformSettings(),
            ]);
            setSummary(usage);
            setSettings(platform);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not load Polaris Assist usage.");
        }
    }, []);

    const loadRuntime = useCallback(async () => {
        setError(null);
        try {
            setRuntime(await fetchAssistRuntimeSettings());
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not load Assist configuration.");
        }
    }, []);

    useEffect(() => {
        if (tab === "usage") {
            void loadUsage(period);
        }
    }, [loadUsage, period, tab]);

    useEffect(() => {
        if (tab === "configuration") {
            void loadRuntime();
        }
    }, [loadRuntime, tab]);

    const saveUsage = async () => {
        if (!settings) return;
        setSaving(true);
        setError(null);
        setNotice(null);
        try {
            const saved = await saveAssistPlatformSettings(settings);
            setSettings(saved);
            setNotice("Platform defaults saved.");
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not save settings.");
        } finally {
            setSaving(false);
        }
    };

    const saveRuntime = async () => {
        if (!runtime) return;
        setSaving(true);
        setError(null);
        setNotice(null);
        try {
            const saved = await saveAssistRuntimeSettings(runtime);
            setRuntime(saved);
            setNotice("Configuration saved. New questions use these settings.");
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not save configuration.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="pa-section" data-testid="polaris-assist-admin-page">
            <h2 className="pa-section-title">Polaris Assist</h2>
            <p className="pa-muted">
                Review token use, or change how Assist retrieves answers and which official websites
                it may search.
            </p>
            {error && <div className="pa-error">{error}</div>}
            {notice && <div className="pa-success">{notice}</div>}

            <div className="pa-tabs" role="tablist" aria-label="Polaris Assist sections">
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "usage"}
                    className={`pa-tab${tab === "usage" ? " active" : ""}`}
                    onClick={() => {
                        setNotice(null);
                        setTab("usage");
                    }}
                >
                    Usage
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "configuration"}
                    className={`pa-tab${tab === "configuration" ? " active" : ""}`}
                    onClick={() => {
                        setNotice(null);
                        setTab("configuration");
                    }}
                >
                    Configuration
                </button>
            </div>

            {tab === "usage" ? (
                <UsageTab
                    period={period}
                    onPeriod={setPeriod}
                    summary={summary}
                    settings={settings}
                    setSettings={setSettings}
                    saving={saving}
                    onSave={saveUsage}
                />
            ) : (
                <ConfigurationTab
                    runtime={runtime}
                    setRuntime={setRuntime}
                    saving={saving}
                    onSave={saveRuntime}
                />
            )}
        </section>
    );
}

function UsageTab({
    period,
    onPeriod,
    summary,
    settings,
    setSettings,
    saving,
    onSave,
}: {
    period: Period;
    onPeriod: (period: Period) => void;
    summary: UsageSummary | null;
    settings: PlatformAiSettings | null;
    setSettings: React.Dispatch<React.SetStateAction<PlatformAiSettings | null>>;
    saving: boolean;
    onSave: () => void;
}) {
    return (
        <>
            <p className="pa-muted">
                Platform-wide token usage and default limits. To review a person or change a school’s
                limits, use <strong>Actions → View AI usage</strong> on the Users or Companies pages.
            </p>
            <div className="pa-period-toggle" role="tablist" aria-label="Cost period">
                {(["daily", "monthly", "yearly"] as Period[]).map((item) => (
                    <button
                        key={item}
                        type="button"
                        role="tab"
                        aria-selected={period === item}
                        className={`pa-page-btn${period === item ? " active" : ""}`}
                        onClick={() => onPeriod(item)}
                    >
                        {item[0].toUpperCase() + item.slice(1)}
                    </button>
                ))}
            </div>

            <div className="stats-grid pa-assist-stats">
                <div className="stat-card">
                    <span className="stat-label">Tokens used</span>
                    <span className="stat-value">{(summary?.totalTokens ?? 0).toLocaleString()}</span>
                </div>
                <div className="stat-card">
                    <span className="stat-label">Running cost</span>
                    <span className="stat-value">{formatUsd(summary?.costUsd)}</span>
                </div>
            </div>

            {settings && (
                <form
                    className="pa-assist-settings"
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSave();
                    }}
                >
                    <h3>Platform defaults</h3>
                    <div className="pa-assist-grid">
                        {[...assistLimitFields("platform"), ...assistRateFields].map(({ key, label, hint }) => (
                            <label key={key} className="pa-assist-field">
                                {label}
                                <input
                                    className="pa-filter-input"
                                    type="number"
                                    min={0}
                                    step={key.includes("Usd") ? "0.01" : "1"}
                                    value={settings[key]}
                                    aria-describedby={`${key}-hint`}
                                    onChange={(e) =>
                                        setSettings((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      [key]: e.target.value.includes(".")
                                                          ? Number(e.target.value)
                                                          : Number.parseInt(e.target.value, 10) || 0,
                                                  }
                                                : prev,
                                        )
                                    }
                                />
                                <span id={`${key}-hint`} className="pa-assist-field-hint">
                                    {hint}
                                </span>
                            </label>
                        ))}
                    </div>
                    <button type="submit" className="pa-page-btn active" disabled={saving}>
                        {saving ? "Saving…" : "Save platform defaults"}
                    </button>
                </form>
            )}
        </>
    );
}

function ConfigurationTab({
    runtime,
    setRuntime,
    saving,
    onSave,
}: {
    runtime: AssistRuntimeSettings | null;
    setRuntime: React.Dispatch<React.SetStateAction<AssistRuntimeSettings | null>>;
    saving: boolean;
    onSave: () => void;
}) {
    if (!runtime) {
        return <p className="pa-muted">Loading configuration…</p>;
    }

    return (
        <form
            className="pa-assist-settings"
            onSubmit={(e) => {
                e.preventDefault();
                onSave();
            }}
        >
            <div className="pa-assist-card">
                <div className="pa-assist-card-header">
                    <div>
                        <h3>Retrieval</h3>
                        <p className="pa-assist-card-lead">
                            How Assist splits documents and how many matching passages it uses.
                        </p>
                    </div>
                </div>
                <div className="pa-assist-grid">
                    <NumberField
                        label="Chunk size"
                        hint="Characters in each knowledge-base passage."
                        value={runtime.chunkSize}
                        onChange={(chunkSize) => setRuntime((prev) => (prev ? { ...prev, chunkSize } : prev))}
                    />
                    <NumberField
                        label="Chunk overlap"
                        hint="Characters shared between neighbouring passages."
                        value={runtime.chunkOverlap}
                        onChange={(chunkOverlap) =>
                            setRuntime((prev) => (prev ? { ...prev, chunkOverlap } : prev))
                        }
                    />
                    <NumberField
                        label="Results per question"
                        hint="How many knowledge-base passages Assist may use."
                        value={runtime.topKResults}
                        onChange={(topKResults) =>
                            setRuntime((prev) => (prev ? { ...prev, topKResults } : prev))
                        }
                    />
                    <NumberField
                        label="Similarity threshold"
                        hint="How closely a passage must match a question (0 to 1)."
                        value={runtime.similarityThreshold}
                        step="0.01"
                        onChange={(similarityThreshold) =>
                            setRuntime((prev) => (prev ? { ...prev, similarityThreshold } : prev))
                        }
                    />
                </div>
            </div>

            <div className="pa-assist-card">
                <div className="pa-assist-card-header">
                    <div>
                        <h3>Embeddings</h3>
                        <p className="pa-assist-card-lead">
                            How documents are turned into searchable passages when you upload them.
                        </p>
                    </div>
                </div>
                <div className="pa-assist-grid">
                    <NumberField
                        label="Embedding batch size"
                        hint="Passages sent together when a document is uploaded."
                        value={runtime.embeddingBatchSize}
                        onChange={(embeddingBatchSize) =>
                            setRuntime((prev) => (prev ? { ...prev, embeddingBatchSize } : prev))
                        }
                    />
                    <NumberField
                        label="Embedding retries"
                        hint="How many times Assist retries a failed embedding request."
                        value={runtime.embeddingMaxRetries}
                        onChange={(embeddingMaxRetries) =>
                            setRuntime((prev) => (prev ? { ...prev, embeddingMaxRetries } : prev))
                        }
                    />
                    <NumberField
                        label="Delay between batches (ms)"
                        hint="Pause between embedding batches, in milliseconds."
                        value={runtime.embeddingInterBatchDelayMs}
                        onChange={(embeddingInterBatchDelayMs) =>
                            setRuntime((prev) => (prev ? { ...prev, embeddingInterBatchDelayMs } : prev))
                        }
                    />
                </div>
            </div>

            <div className="pa-assist-card">
                <div className="pa-assist-card-header">
                    <div>
                        <h3>Guardrails</h3>
                        <p className="pa-assist-card-lead">
                            Topic checks and how often athletes or staff may ask questions.
                        </p>
                    </div>
                </div>
                <label className="pa-assist-toggle">
                    <input
                        type="checkbox"
                        checked={runtime.guardrailsEnabled}
                        onChange={(e) =>
                            setRuntime((prev) =>
                                prev ? { ...prev, guardrailsEnabled: e.target.checked } : prev,
                            )
                        }
                    />
                    <span>Turn on guardrails</span>
                </label>
                <label className="pa-assist-toggle">
                    <input
                        type="checkbox"
                        checked={runtime.topicClassificationEnabled}
                        onChange={(e) =>
                            setRuntime((prev) =>
                                prev ? { ...prev, topicClassificationEnabled: e.target.checked } : prev,
                            )
                        }
                    />
                    <span>Check whether a question is on topic</span>
                </label>
                <div className="pa-assist-grid">
                    <NumberField
                        label="Longest message"
                        hint="Maximum characters in one question."
                        value={runtime.maxMessageLength}
                        onChange={(maxMessageLength) =>
                            setRuntime((prev) => (prev ? { ...prev, maxMessageLength } : prev))
                        }
                    />
                    <NumberField
                        label="Athlete hourly limit"
                        hint="Questions an athlete or guardian can ask each hour."
                        value={runtime.athleteHourlyLimit}
                        onChange={(athleteHourlyLimit) =>
                            setRuntime((prev) => (prev ? { ...prev, athleteHourlyLimit } : prev))
                        }
                    />
                    <NumberField
                        label="Athlete daily limit"
                        hint="Questions an athlete or guardian can ask each day."
                        value={runtime.athleteDailyLimit}
                        onChange={(athleteDailyLimit) =>
                            setRuntime((prev) => (prev ? { ...prev, athleteDailyLimit } : prev))
                        }
                    />
                    <NumberField
                        label="Staff hourly limit"
                        hint="Questions staff can ask each hour."
                        value={runtime.staffHourlyLimit}
                        onChange={(staffHourlyLimit) =>
                            setRuntime((prev) => (prev ? { ...prev, staffHourlyLimit } : prev))
                        }
                    />
                    <NumberField
                        label="Staff daily limit"
                        hint="Questions staff can ask each day."
                        value={runtime.staffDailyLimit}
                        onChange={(staffDailyLimit) =>
                            setRuntime((prev) => (prev ? { ...prev, staffDailyLimit } : prev))
                        }
                    />
                </div>
            </div>

            <div className="pa-assist-card">
                <div className="pa-assist-card-header">
                    <div>
                        <h3>External data and web search</h3>
                        <p className="pa-assist-card-lead">
                            College Scorecard lookup and the official websites Assist may search.
                        </p>
                    </div>
                </div>
                <label className="pa-assist-toggle">
                    <input
                        type="checkbox"
                        checked={runtime.externalDataEnabled}
                        onChange={(e) =>
                            setRuntime((prev) =>
                                prev ? { ...prev, externalDataEnabled: e.target.checked } : prev,
                            )
                        }
                    />
                    <span>Use live College Scorecard data</span>
                </label>
                <label className="pa-assist-toggle">
                    <input
                        type="checkbox"
                        checked={runtime.webSearchEnabled}
                        onChange={(e) =>
                            setRuntime((prev) =>
                                prev ? { ...prev, webSearchEnabled: e.target.checked } : prev,
                            )
                        }
                    />
                    <span>Allow official web search</span>
                </label>
                <div className="pa-assist-grid">
                    <NumberField
                        label="Web searches per question"
                        hint="How many web searches Assist may run for one question."
                        value={runtime.webSearchMaxUses}
                        onChange={(webSearchMaxUses) =>
                            setRuntime((prev) => (prev ? { ...prev, webSearchMaxUses } : prev))
                        }
                    />
                    <label className="pa-assist-field">
                        Web search model
                        <input
                            className="pa-filter-input"
                            type="text"
                            value={runtime.webSearchModel}
                            aria-describedby="webSearchModel-hint"
                            onChange={(e) =>
                                setRuntime((prev) =>
                                    prev ? { ...prev, webSearchModel: e.target.value } : prev,
                                )
                            }
                        />
                        <span id="webSearchModel-hint" className="pa-assist-field-hint">
                            Leave blank to use the default Assist model.
                        </span>
                    </label>
                    <label className="pa-assist-field">
                        Web search tool
                        <input
                            className="pa-filter-input"
                            type="text"
                            value={runtime.webSearchToolType}
                            aria-describedby="webSearchToolType-hint"
                            onChange={(e) =>
                                setRuntime((prev) =>
                                    prev ? { ...prev, webSearchToolType: e.target.value } : prev,
                                )
                            }
                        />
                        <span id="webSearchToolType-hint" className="pa-assist-field-hint">
                            Anthropic web-search tool name.
                        </span>
                    </label>
                </div>
                <AssistAllowedDomainsField
                    domains={runtime.allowedDomains}
                    onChange={(allowedDomains) =>
                        setRuntime((prev) => (prev ? { ...prev, allowedDomains } : prev))
                    }
                />
            </div>

            <div className="pa-assist-actions">
                <button type="submit" className="pa-page-btn active" disabled={saving}>
                    {saving ? "Saving…" : "Save configuration"}
                </button>
            </div>
        </form>
    );
}

function NumberField({
    label,
    hint,
    value,
    step = "1",
    onChange,
}: {
    label: string;
    hint: string;
    value: number;
    step?: string;
    onChange: (value: number) => void;
}) {
    const id = `${label.replace(/\s+/g, "-").toLowerCase()}-hint`;
    return (
        <label className="pa-assist-field">
            {label}
            <input
                className="pa-filter-input"
                type="number"
                min={0}
                step={step}
                value={value}
                aria-describedby={id}
                onChange={(e) =>
                    onChange(step.includes(".") ? Number(e.target.value) : Number.parseInt(e.target.value, 10) || 0)
                }
            />
            <span id={id} className="pa-assist-field-hint">
                {hint}
            </span>
        </label>
    );
}
